import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// Structured logging helper (dev only)
const isDev = import.meta.env.DEV;
const log = (hook: string, stage: string, data?: unknown) => {
  if (isDev) {
    console.log(`[${hook}] ${stage}`, data || '');
  }
};

export interface HealthScoreData {
  healthScore: number;
  trend: 'up' | 'down' | 'stable';
  delta: number;
  metrics: {
    messagesSent: number;
    repliesReceived: number;
    replyRate: number;
    meetingsBooked: number;
    conversionRate: number;
  };
  period: {
    startDate: string;
    endDate: string;
  };
  previous: {
    messagesSent: number;
    repliesReceived: number;
    replyRate: number;
    meetingsBooked: number;
    conversionRate: number;
  };
}

export interface MetricsData {
  period: { startDate: string; endDate: string };
  filters: { channel?: string; methodology?: string; campaignId?: string };
  current: {
    messagesSent: number;
    repliesReceived: number;
    replyRate: number;
    meetingsBooked: number;
    qualifiedLeads: number;
    conversionRate: number;
    totalCostUsd: number;
    costPerMeeting: number;
    costPerReply: number;
  };
  previous: {
    messagesSent: number;
    repliesReceived: number;
    replyRate: number;
    meetingsBooked: number;
    qualifiedLeads: number;
    conversionRate: number;
    totalCostUsd: number;
    costPerMeeting: number;
    costPerReply: number;
  };
  delta: {
    messagesSent: number;
    repliesReceived: number;
    replyRate: number;
    meetingsBooked: number;
    qualifiedLeads: number;
    conversionRate: number;
    totalCostUsd: number;
    costPerMeeting: number;
    costPerReply: number;
  };
}

export interface MorningBriefData {
  date: string;
  health_score: number;
  health_trend: 'up' | 'down' | 'stable';
  metrics: {
    messages_sent: { value: number; delta: number };
    reply_rate: { value: number; delta: number };
    meetings_booked: { value: number; delta: number };
  };
  priority_actions: Array<{
    type: 'follow_up' | 'approve_draft' | 'review_reply' | 'at_risk';
    contact_name: string;
    company: string;
    urgency: 'high' | 'medium' | 'low';
    action_text: string;
    deep_link: string;
  }>;
  insights: Array<{
    type: 'pattern' | 'anomaly' | 'recommendation';
    title: string;
    description: string;
    evidence?: string;
  }>;
  forecast: {
    meetings_this_month: { current: number; target: number; predicted: number };
    pipeline_value: number;
  };
}

export interface GhostRiskItem {
  draftId: string;
  contactId: string | null;
  leadName: string;
  leadCompany: string;
  daysInactive: number;
  ghostProbability: number;
  recommendedAction: string;
}

export interface ForecastRow {
  forecastDate: string;
  predictedReplies: number;
  predictedMeetings: number;
  predictedRevenue: number;
  confidenceIntervalLow: number;
  confidenceIntervalHigh: number;
}

export interface WinLossRow {
  methodologyUsed: string | null;
  outcome: string;
  count: number;
  avgDealValue: number;
  primaryReason: string | null;
  winRate: number;
  bestMethodology: string | null;
  avgCycleDays: number | null;
}

const getAccessToken = async (): Promise<string> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return session.access_token;
};

const fetchWithAuth = async <T,>(url: string): Promise<T> => {
  const accessToken = await getAccessToken();
  
  // AbortController for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  try {
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      const errorMessage = 
        errorBody?.error?.message || 
        errorBody?.message || 
        `API error: ${response.status}`;
      throw new Error(errorMessage);
    }

    return response.json();
  } catch (err) {
    clearTimeout(timeoutId);
    
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error(`Timeout after 10s - ${url}`);
    }
    
    throw err;
  }
};

export const useHealthScore = (days = 7): UseQueryResult<HealthScoreData, Error> => {
  return useQuery({
    queryKey: ['health-score', days],
    queryFn: async (): Promise<HealthScoreData> => {
      log('HealthScore', 'query:start', { days });
      try {
        const data = await fetchWithAuth<HealthScoreData>(`/api/analytics/health-score?days=${days}`);
        log('HealthScore', 'query:success', { score: data.healthScore });
        return data;
      } catch (err) {
        log('HealthScore', 'query:error', err instanceof Error ? err.message : String(err));
        throw err;
      }
    },
    staleTime: 60 * 1000,
  });
};

export const useAnalyticsMetrics = (
  params: { days?: number; channel?: string; methodology?: string; campaignId?: string } = {}
): UseQueryResult<MetricsData, Error> => {
  const queryParams = new URLSearchParams();
  if (params.days) queryParams.set('days', String(params.days));
  if (params.channel) queryParams.set('channel', params.channel);
  if (params.methodology) queryParams.set('methodology', params.methodology);
  if (params.campaignId) queryParams.set('campaign_id', params.campaignId);

  return useQuery({
    queryKey: ['analytics-metrics', params.days, params.channel, params.methodology, params.campaignId],
    queryFn: async (): Promise<MetricsData> => {
      const suffix = queryParams.toString();
      const url = suffix ? `/api/analytics/metrics?${suffix}` : '/api/analytics/metrics';
      return fetchWithAuth<MetricsData>(url);
    },
    staleTime: 60 * 1000,
  });
};

export const useMorningBrief = (): UseQueryResult<MorningBriefData, Error> => {
  return useQuery({
    queryKey: ['morning-brief'],
    queryFn: async (): Promise<MorningBriefData> => {
      log('MorningBrief', 'query:start');
      try {
        const data = await fetchWithAuth<MorningBriefData>('/api/analytics/morning-brief');
        log('MorningBrief', 'query:success', { actionsCount: data.priority_actions.length });
        return data;
      } catch (err) {
        log('MorningBrief', 'query:error', err instanceof Error ? err.message : String(err));
        throw err;
      }
    },
    staleTime: 60 * 1000,
  });
};

export const useGhostRisk = (limit = 8): UseQueryResult<{ items: GhostRiskItem[] }, Error> => {
  return useQuery({
    queryKey: ['ghost-risk', limit],
    queryFn: async (): Promise<{ items: GhostRiskItem[] }> => {
      log('GhostRisk', 'query:start', { limit });
      try {
        const data = await fetchWithAuth<{ items: GhostRiskItem[] }>(`/api/intelligence/ghost-risk?limit=${limit}`);
        log('GhostRisk', 'query:success', { itemsCount: data.items.length });
        return data;
      } catch (err) {
        log('GhostRisk', 'query:error', err instanceof Error ? err.message : String(err));
        throw err;
      }
    },
    staleTime: 60 * 1000,
  });
};

export const useForecast = (days = 30): UseQueryResult<{ rows: ForecastRow[]; days: number }, Error> => {
  return useQuery({
    queryKey: ['forecast', days],
    queryFn: async (): Promise<{ rows: ForecastRow[]; days: number }> => {
      log('Forecast', 'query:start', { days });
      try {
        const data = await fetchWithAuth<{ rows: ForecastRow[]; days: number }>(
          `/api/intelligence/forecast?days=${days}`
        );
        log('Forecast', 'query:success', { rowsCount: data.rows.length });
        return data;
      } catch (err) {
        log('Forecast', 'query:error', err instanceof Error ? err.message : String(err));
        throw err;
      }
    },
    staleTime: 60 * 1000,
  });
};

export const useWinLoss = (): UseQueryResult<{ rows: WinLossRow[] }, Error> => {
  return useQuery({
    queryKey: ['win-loss'],
    queryFn: async (): Promise<{ rows: WinLossRow[] }> => {
      log('WinLoss', 'query:start');
      try {
        const data = await fetchWithAuth<{ rows: WinLossRow[] }>('/api/intelligence/win-loss');
        log('WinLoss', 'query:success', { rowsCount: data.rows.length });
        return data;
      } catch (err) {
        log('WinLoss', 'query:error', err instanceof Error ? err.message : String(err));
        throw err;
      }
    },
    staleTime: 60 * 1000,
  });
};
